import * as React from "react";
import { useParams, Link } from "react-router";
import { useOne, useInvalidate, useNotification, CanAccess } from "@refinedev/core";
import { useDataProvider } from "@refinedev/core";
import { AuditBanner } from "@/components/AuditBanner";
import { AccountStatusPill } from "@/components/AccountStatusPill";
import { SkeletonTable } from "@/components/SkeletonTable";
import { ErrorBanner } from "@/components/ErrorBanner";
import { BanModal } from "@/components/BanModal";
import { GrantModal } from "@/components/GrantModal";
import { ForceEndModal } from "@/components/ForceEndModal";
import { UnbanModal } from "@/components/UnbanModal";
import { SubscriptionTimeline } from "@/components/SubscriptionTimeline";
import { UserContentTab } from "@/components/UserContentTab";
import { Button } from "@/components/ui/button";

type AccountStatus = "active" | "banned";

interface UserDetail {
  id: string;
  email: string;
  registeredAt: string;
  accountStatus: AccountStatus;
  bannedUntil?: string | null;
  banReason?: string | null;
  entitlement?: {
    isPremium: boolean;
    expiresAt: string | null;
    source: "apphud" | "override" | "none";
  };
}

type ActiveModal = "grant" | "ban" | "unban" | "force-end" | null;

function UserActivityTab({ userId }: { userId: string }): React.JSX.Element {
  return (
    <div className="py-4 text-[13px] text-[var(--muted)]">
      История активности пользователя {userId} — будет доступна в следующей итерации.
    </div>
  );
}

function PiiSection({ user }: { user: UserDetail }): React.JSX.Element {
  const formattedRegistered = user.registeredAt
    ? new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(user.registeredAt))
    : "—";

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] p-5">
      <h3 className="text-[14px] font-[600] mb-3.5">Персональные данные</h3>
      <dl className="grid grid-cols-3 gap-4">
        <div>
          <dt className="text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-1">
            ID
          </dt>
          <dd className="text-[13px] font-mono text-[var(--muted)] bg-[var(--surface-2)] border border-[var(--border)] px-2 py-0.5 rounded-[4px] w-fit">
            {user.id}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-1">
            Email
          </dt>
          <dd className="text-[14px] font-[500]">{user.email}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-[600] uppercase tracking-wide text-[var(--muted)] mb-1">
            Дата регистрации
          </dt>
          <dd className="text-[14px] font-[500]">{formattedRegistered}</dd>
        </div>
      </dl>
    </div>
  );
}

export function UsersShow(): React.JSX.Element {
  const { id: userId } = useParams<{ id: string }>();
  const invalidate = useInvalidate();
  const { open: openNotification } = useNotification();
  const dataProvider = useDataProvider();
  const dp = dataProvider();

  const [activeTab, setActiveTab] = React.useState<"subscription" | "activity" | "content">("subscription");
  const [activeModal, setActiveModal] = React.useState<ActiveModal>(null);

  const { query } = useOne<UserDetail>({
    resource: "users",
    id: userId ?? "",
    queryOptions: { enabled: Boolean(userId) },
  });

  const data = query.data;
  const isLoading = query.isLoading;
  const isError = query.isError;
  const refetch = query.refetch;

  const user = data?.data;

  async function invalidateUser(): Promise<void> {
    await invalidate({ resource: "users", id: userId, invalidates: ["detail"] });
  }

  async function handleBan(params: {
    reason: string;
  }): Promise<void> {
    await dp.custom?.({
      url: `/admin/users/${userId ?? ""}/ban`,
      method: "post",
      payload: { reason: params.reason },
    });
    await invalidateUser();
    openNotification?.({
      type: "success",
      message: "Пользователь заблокирован",
      description: "Статус аккаунта обновлён",
    });
  }

  async function handleUnban(params: { reason?: string }): Promise<void> {
    await dp.custom?.({
      url: `/admin/users/${userId ?? ""}/unban`,
      method: "post",
      payload: { reason: params.reason },
    });
    await invalidateUser();
    openNotification?.({
      type: "success",
      message: "Пользователь разблокирован",
      description: "Статус аккаунта обновлён",
    });
  }

  async function handleGrant(params: {
    plan: string;
    grantUntil: string;
    reason: string;
  }): Promise<void> {
    await dp.custom?.({
      url: `/admin/users/${userId ?? ""}/entitlement/grant`,
      method: "post",
      payload: {
        productId: params.plan,
        grantUntil: new Date(params.grantUntil).toISOString(),
        reason: params.reason,
      },
    });
    await invalidate({
      resource: `users/${userId ?? ""}/entitlement`,
      invalidates: ["detail"],
    });
    openNotification?.({
      type: "success",
      message: "Компенсация выдана",
      description: "Подписка пользователя обновлена",
    });
  }

  async function handleForceEnd(params: { reason: string }): Promise<void> {
    await dp.custom?.({
      url: `/admin/users/${userId ?? ""}/entitlement/force-end`,
      method: "post",
      payload: { reason: params.reason },
    });
    await invalidate({
      resource: `users/${userId ?? ""}/entitlement`,
      invalidates: ["detail"],
    });
    openNotification?.({
      type: "success",
      message: "Подписка завершена",
      description: "Подписка пользователя принудительно завершена",
    });
  }

  if (isLoading) {
    return (
      <div className="p-6 md:p-8 max-w-4xl">
        <div className="h-4 w-32 bg-[var(--surface-2)] rounded animate-pulse mb-5" />
        <div className="h-7 w-48 bg-[var(--surface-2)] rounded animate-pulse mb-6" />
        <SkeletonTable columns={4} rows={4} />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="p-6 md:p-8 max-w-4xl">
        <ErrorBanner
          message="Не удалось загрузить данные пользователя"
          onRetry={() => { void refetch(); }}
        />
      </div>
    );
  }

  const isBanned = user.accountStatus === "banned";

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <nav className="flex items-center gap-1.5 text-[13px] text-[var(--muted)] mb-5" aria-label="Навигация">
        <Link
          to="/users"
          className="text-[var(--accent)] font-[500] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] rounded"
        >
          Пользователи
        </Link>
        <span className="text-[var(--border)]">/</span>
        <span className="text-[var(--fg)] font-[500]">{user.email}</span>
      </nav>

      <AuditBanner />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-[22px] font-[700] tracking-tight">{user.email}</h2>
            <span className="font-mono text-[12px] text-[var(--muted)] bg-[var(--surface-2)] border border-[var(--border)] px-2 py-0.5 rounded-[4px]">
              {user.id}
            </span>
            <AccountStatusPill status={user.accountStatus} />
          </div>
        </div>

        <div className="flex items-start gap-2 flex-shrink-0">
          <CanAccess resource="users" action="grant">
            <Button
              type="button"
              variant="outline"
              onClick={() => setActiveModal("grant")}
            >
              Выдать компенсацию
            </Button>
          </CanAccess>

          {isBanned ? (
            <CanAccess resource="users" action="unban">
              <Button
                type="button"
                variant="default"
                onClick={() => setActiveModal("unban")}
              >
                Разблокировать
              </Button>
            </CanAccess>
          ) : (
            <CanAccess resource="users" action="ban">
              <Button
                type="button"
                variant="destructive"
                onClick={() => setActiveModal("ban")}
                className="border border-[var(--danger)] text-[var(--danger)] bg-transparent hover:bg-[rgba(196,58,42,0.06)]"
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
                Заблокировать
              </Button>
            </CanAccess>
          )}

          <CanAccess resource="users" action="force_end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setActiveModal("force-end")}
            >
              Завершить подписку
            </Button>
          </CanAccess>
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Информация о пользователе"
        className="flex border-b border-[var(--border)] mb-6 gap-0"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "subscription"}
          aria-controls="tab-panel-subscription"
          id="tab-subscription"
          className={[
            "px-5 h-10 min-h-[44px] text-[13px] font-[500] border-b-2 -mb-px transition-colors",
            activeTab === "subscription"
              ? "text-[var(--accent)] border-[var(--accent)] font-[600]"
              : "text-[var(--muted)] border-transparent hover:text-[var(--fg)]",
          ].join(" ")}
          onClick={() => setActiveTab("subscription")}
        >
          Подписка
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "activity"}
          aria-controls="tab-panel-activity"
          id="tab-activity"
          className={[
            "px-5 h-10 min-h-[44px] text-[13px] font-[500] border-b-2 -mb-px transition-colors",
            activeTab === "activity"
              ? "text-[var(--accent)] border-[var(--accent)] font-[600]"
              : "text-[var(--muted)] border-transparent hover:text-[var(--fg)]",
          ].join(" ")}
          onClick={() => setActiveTab("activity")}
        >
          Последние действия
        </button>
        <CanAccess resource="users" action="view_content">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "content"}
            aria-controls="tab-panel-content"
            id="tab-content"
            className={[
              "px-5 h-10 min-h-[44px] text-[13px] font-[500] border-b-2 -mb-px transition-colors",
              activeTab === "content"
                ? "text-[var(--accent)] border-[var(--accent)] font-[600]"
                : "text-[var(--muted)] border-transparent hover:text-[var(--fg)]",
            ].join(" ")}
            onClick={() => setActiveTab("content")}
          >
            Контент
          </button>
        </CanAccess>
      </div>

      <div className="grid grid-cols-[1fr_300px] gap-5 items-start">
        <div>
          <div
            id="tab-panel-subscription"
            role="tabpanel"
            aria-labelledby="tab-subscription"
            hidden={activeTab !== "subscription"}
          >
            {activeTab === "subscription" && userId && (
              <SubscriptionTimeline userId={userId} />
            )}
          </div>
          <div
            id="tab-panel-activity"
            role="tabpanel"
            aria-labelledby="tab-activity"
            hidden={activeTab !== "activity"}
          >
            {activeTab === "activity" && userId && (
              <UserActivityTab userId={userId} />
            )}
          </div>
          <div
            id="tab-panel-content"
            role="tabpanel"
            aria-labelledby="tab-content"
            hidden={activeTab !== "content"}
          >
            {activeTab === "content" && userId && (
              <UserContentTab userId={userId} />
            )}
          </div>
        </div>

        <div>
          <PiiSection user={user} />
        </div>
      </div>

      {userId && (
        <>
          <BanModal
            open={activeModal === "ban"}
            userId={userId}
            userEmail={user.email}
            onBan={handleBan}
            onClose={() => setActiveModal(null)}
          />
          <UnbanModal
            open={activeModal === "unban"}
            userId={userId}
            userEmail={user.email}
            onUnban={handleUnban}
            onClose={() => setActiveModal(null)}
          />
          <GrantModal
            open={activeModal === "grant"}
            userId={userId}
            onGrant={handleGrant}
            onClose={() => setActiveModal(null)}
          />
          <ForceEndModal
            open={activeModal === "force-end"}
            userId={userId}
            onForceEnd={handleForceEnd}
            onClose={() => setActiveModal(null)}
          />
        </>
      )}
    </div>
  );
}
